import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Facebook, 
  Instagram, 
  Twitter, 
  Youtube,
  MessageCircle,
  Linkedin
} from 'lucide-react';
import { motion } from 'framer-motion';
import { appConfig } from 'config/appConfig';
import WhatsAppGroupButton from '../ui/WhatsAppGroupButton';
import KannadaTagline from '../ui/KannadaTagline';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  // Map of social media platforms to their icons and colors
  const socialMediaConfig: Record<string, { icon: any; color: string; label: string }> = {
    facebook: { 
      icon: Facebook, 
      color: 'hover:text-blue-600',
      label: 'Facebook'
    },
    instagram: { 
      icon: Instagram, 
      color: 'hover:text-pink-600',
      label: 'Instagram'
    },
    twitter: { 
      icon: Twitter, 
      color: 'hover:text-blue-400',
      label: 'Twitter'
    },
    linkedin: { 
      icon: Linkedin, 
      color: 'hover:text-blue-700',
      label: 'LinkedIn'
    },
    youtube: { 
      icon: Youtube, 
      color: 'hover:text-red-600',
      label: 'YouTube'
    },
    whatsapp: { 
      icon: MessageCircle, 
      color: 'hover:text-green-600',
      label: 'WhatsApp'
    },
  };

  // Generate social links from appConfig - only include platforms with non-empty URLs
  const socialLinks = Object.entries(appConfig.social)
    .filter(([_, url]) => url && url.trim() !== '')
    .map(([platform, url]) => {
      const config = socialMediaConfig[platform];
      if (!config) return null;
      return {
        icon: config.icon,
        href: url,
        label: config.label,
        color: config.color,
      };
    })
    .filter((link): link is { icon: any; href: string; label: string; color: string } => link !== null);

  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'Tours', href: '/tours' },
    { label: 'Gallery', href: '/gallery' },
    { label: 'Cancellation Policy', href: appConfig.links.refundPolicy },
    { label: 'Online Payment', href: '/payment' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <footer className="gradient-footer text-neutral-white">
      <motion.div 
        className="container py-2xl"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 xl:gap-16 items-start">
          {/* Company Info */}
          <motion.div variants={itemVariants} className="space-y-6">
            <div>
              <Link to="/" className="inline-block mb-4" aria-label="Go to home">
                <img
                  src="/images/bg_raise_logo.png"
                  alt="Raise"
                  className="block h-12 md:h-14 lg:h-16 w-auto"
                />
              </Link>
              <p className="body-regular text-neutral-light-gray leading-relaxed">
                {appConfig.company.tagline}
              </p>
            </div>
            
            {/* Social Media Links - Only show if there are links configured */}
            {socialLinks.length > 0 && (
              <div>
                <h4 className="heading-quaternary mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  {socialLinks.map(({ icon: Icon, href, label, color }) => (
                    <a
                      key={label}
                      href={href}
                      className={`p-2 bg-white bg-opacity-10 rounded-full transition-all duration-300 hover:bg-opacity-20 ${color}`}
                      aria-label={label}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon size={20} />
                    </a>
                  ))}
                </div>
                <div className="mt-4">
                  <WhatsAppGroupButton size="sm" />
                </div>
              </div>
            )}
          </motion.div>

          {/* Contact Information */}
          <motion.div variants={itemVariants} className="md:order-2">
            <h4 className="heading-quaternary text-accent-champagne mb-6">
              Contact Info
            </h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin size={20} className="mt-1 text-accent-rose-gold flex-shrink-0" />
                <p className="body-small text-neutral-light-gray">
                  {appConfig.contact.address.line1}<br />
                  {appConfig.contact.address.line2}<br />
                  {appConfig.contact.address.line3}<br />
                  {appConfig.contact.address.country}
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Phone size={18} className="text-accent-rose-gold flex-shrink-0" />
                <a 
                  href={`tel:${appConfig.contact.phone.replace(/\s/g, '')}`}
                  className="body-small text-neutral-light-gray hover:text-accent-champagne transition-colors"
                >
                  {appConfig.contact.phone}
                </a>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail size={18} className="text-accent-rose-gold flex-shrink-0" />
                <a 
                  href={`mailto:${appConfig.contact.email}`}
                  className="body-small text-neutral-light-gray hover:text-accent-champagne transition-colors"
                >
                  {appConfig.contact.email}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants} className="md:justify-self-center">
            <h4 className="heading-quaternary text-accent-champagne mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="body-regular text-neutral-light-gray hover:text-accent-champagne transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

        </div>

        {/* Bottom Bar */}
        <motion.div 
          variants={itemVariants}
          className="border-t border-white border-opacity-20 mt-xl pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6">
            <p className="body-small text-neutral-light-gray text-center md:text-left">
              © {currentYear} RAISE Tours. All rights reserved.
            </p>
            
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <Link 
                to="/privacy-policy" 
                className="body-small text-neutral-light-gray hover:text-accent-champagne transition-colors"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms-of-service" 
                className="body-small text-neutral-light-gray hover:text-accent-champagne transition-colors"
              >
                Terms of Service
              </Link>
              <Link 
                to={appConfig.links.refundPolicy} 
                className="body-small text-neutral-light-gray hover:text-accent-champagne transition-colors"
              >
                Cancellation Policy
              </Link>
            </div>

            <KannadaTagline
              imgClassName="h-12 md:h-14 w-auto max-w-full rounded-md"
            />
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
};

export default Footer;
